import { Grid, Container, Typography } from '@mui/material';
import ODSIcon from '@telekom-ods/react-icons/ODSIcon';
import { Formik, Form } from 'formik';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';
import * as Yup from 'yup';

import Button from '../../components/Buttons/Button';
import { FormikCheckbox, FormikPasswordField } from '../../components/Formik';
import { isValidPassword } from '../../helpers/validation';

interface SetPasswordFormValues {
  password: string;
  passwordConfirm: string;
  termsAccepted: boolean;
}

interface SetPasswordProps {
  onSubmit: (password: string) => void | Promise<void>;
  organizationName: string;
}

const terms = (
  <>
    <Typography gutterBottom>
      By accessing this web portal, You explicitly confirm and acknowledge that You are officially
      authorized and designated as &quot; IaaS &quot; user to represent Your Employer Company (as
      &quot;Service Receiver&quot;), whilst undertaking activities in accordance to the General
      Terms and Conditions given on the following &nbsp;
      <Link to='/gtc'> General Terms and Conditions </Link>
      &nbsp; which must be explicitly accepted by You as requested below.
    </Typography>
    <Typography gutterBottom>
      Prior accepting the above stated General Terms and Conditions and Agreement on the Processing
      of Personal Data, please read it carefully and make sure that you understand the terms,
      conditions and provisions thereof, since by accepting these terms and conditions, You
      expressly agree to apply thereof when evaluating &quot;IaaS&quot;.
    </Typography>
    <Typography gutterBottom>
      By accepting the above stated General Terms and Conditions, please be informed that on the
      following &nbsp;
      <Link to='/data-privacy'>Privacy</Link>
      &nbsp; you will find our &quot;Data privacy information for DT Cloud Services portal&quot;,
      for your own informative purposes and awareness, only.
    </Typography>
  </>
);

const SetPassword = ({ onSubmit, organizationName }: SetPasswordProps): React.JSX.Element => {
  const intl = useIntl();
  const validationSchema = Yup.object().shape({
    termsAccepted: Yup.boolean().oneOf([true], 'Required'),
    password: Yup.string()
      .test('Strong password', intl.formatMessage({ id: 'ERR_PASSWORD_INVALID' }), value =>
        value ? isValidPassword(value) : true
      )
      .required('Required'),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password'), undefined], intl.formatMessage({ id: 'userPasswordMismatch' }))
      .required('Required'),
  });

  const initialValues: SetPasswordFormValues = {
    password: '',
    passwordConfirm: '',
    termsAccepted: false,
  };

  const handleSubmit = (data: SetPasswordFormValues): void | Promise<void> => {
    return onSubmit(data.password);
  };

  return (
    <Container maxWidth='md'>
      <Typography
        variant='h4'
        color='primary'
        gutterBottom
      >
        User activation
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <Grid
              container
              direction='column'
              gap={1}
            >
              <Grid
                item
                display='flex'
              >
                <ODSIcon name='communities-type-standard' />
                <Typography>
                  {`You were invited to ${organizationName}. Please create your password.`}
                </Typography>
              </Grid>
              <Grid
                item
                display='flex'
              >
                <ODSIcon name='information-type-standard' />
                <Typography>
                  Read about DT Cloud Services&nbsp;
                  <Link to='/about-us'>here</Link>
                </Typography>
              </Grid>
              <Grid
                container
                direction='column'
                width={300}
                py={2}
              >
                <Grid item>
                  <FormikPasswordField
                    label='Password'
                    name='password'
                  />
                </Grid>
                <Grid item>
                  <FormikPasswordField
                    label='Confirm Password'
                    name='passwordConfirm'
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent='flex-end'
            >
              <Grid item>
                {terms}
                <FormikCheckbox
                  label='Accept'
                  name='termsAccepted'
                />
              </Grid>
              <Grid item>
                <Button
                  type='submit'
                  name='Submit'
                  disabled={isSubmitting || !isValid || !dirty}
                  label='Submit'
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default SetPassword;
